import { ConnectorFormDetails, Input, Label } from "@simplicity/ui";
import { TablesEditor, type Schedules, type TableConfig } from "../tables";
import type { ConnectorFormProps } from "./GenericVarsForm";

type UpdateFn = (key: string, value: string | number) => void;

function TextField(props: {
  id: string;
  label: string;
  value: string;
  onChange: (val: string) => void;
  placeholder: string;
}) {
  return (
    <div className="flex-1">
      <Label htmlFor={props.id} className="form-label">
        {props.label}
      </Label>
      <Input
        id={props.id}
        type="text"
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        placeholder={props.placeholder}
        className="font-mono"
      />
    </div>
  );
}

function ConnectionFields(props: {
  host: string;
  port: number | "";
  region: string;
  sftpWrapperUrl: string;
  updateField: UpdateFn;
}) {
  return (
    <>
      <div className="flex gap-4">
        <TextField
          id="ir-host"
          label="Host"
          value={props.host}
          onChange={(v) => props.updateField("host", v)}
          placeholder="Default: sftp.i-ready.com"
        />
        <div className="flex-1">
          <Label htmlFor="ir-port" className="form-label">
            Port
          </Label>
          <Input
            id="ir-port"
            type="number"
            value={props.port}
            onChange={(e) => {
              const val = e.target.value;
              props.updateField("port", val ? Number(val) : "");
            }}
            placeholder="Default: 22"
            className="font-mono"
          />
        </div>
      </div>
      <TextField
        id="ir-region"
        label="Region"
        value={props.region}
        onChange={(v) => props.updateField("region", v)}
        placeholder="Default: us-central1"
      />
      <TextField
        id="ir-sftp-wrapper-url"
        label="SFTP Wrapper URL"
        value={props.sftpWrapperUrl}
        onChange={(v) => props.updateField("sftpWrapperUrl", v)}
        placeholder="Optional — Cloud Function URL override"
      />
    </>
  );
}

/** Extract typed field values from vars to keep main function simple. */
function readFields(vars: Record<string, unknown>) {
  const str = (k: string) => (vars[k] as string) ?? "";
  const tables = Array.isArray(vars.tables) ? (vars.tables as TableConfig[]) : [];
  const schedules = (
    vars.schedules != null && typeof vars.schedules === "object" && !Array.isArray(vars.schedules)
      ? vars.schedules
      : {}
  ) as Schedules;
  return {
    tables,
    schedules,
    projectId: str("projectId"),
    region: str("region"),
    host: str("host"),
    port: typeof vars.port === "number" ? vars.port : ("" as number | ""),
    username: str("username"),
    sftpWrapperUrl: str("sftpWrapperUrl"),
    bronzeDataset: str("bronzeDataset"),
    silverDataset: str("silverDataset"),
  };
}

export function IReadySftpForm(props: ConnectorFormProps) {
  const f = readFields(props.vars);

  function updateField(key: string, value: string | number) {
    const next = { ...props.vars };
    if (value === "" || value === undefined) {
      delete next[key];
    } else {
      next[key] = value;
    }
    props.onVarsChange(next);
    props.clearBanner();
  }

  return (
    <>
      <div className="flex gap-4">
        <TextField
          id="ir-project-id"
          label="GCP Project ID"
          value={f.projectId}
          onChange={(v) => updateField("projectId", v)}
          placeholder="GCP project ID"
        />
        <TextField
          id="ir-username"
          label="Username"
          value={f.username}
          onChange={(v) => updateField("username", v)}
          placeholder="Required — SFTP username"
        />
      </div>
      <TablesEditor
        tables={f.tables}
        schedules={f.schedules}
        onChange={(change) => {
          props.onVarsChange({ ...props.vars, ...change });
          props.clearBanner();
        }}
        connectorType={props.connectorType}
        connectorId={props.connectorId}
      />
      {!props.hideAdvanced && (
        <ConnectorFormDetails
          summary="Advanced Options"
          className="mt-4 rounded border border-border/50"
        >
          <ConnectionFields
            host={f.host}
            port={f.port}
            region={f.region}
            sftpWrapperUrl={f.sftpWrapperUrl}
            updateField={updateField}
          />
          <div className="flex gap-4">
            <TextField
              id="ir-bronze-dataset"
              label="Bronze Dataset"
              value={f.bronzeDataset}
              onChange={(v) => updateField("bronzeDataset", v)}
              placeholder={`Default: bronze__${props.connectorPrefix}`}
            />
            <TextField
              id="ir-silver-dataset"
              label="Silver Dataset"
              value={f.silverDataset}
              onChange={(v) => updateField("silverDataset", v)}
              placeholder={`Default: silver__${props.connectorPrefix}`}
            />
          </div>
        </ConnectorFormDetails>
      )}
    </>
  );
}
